import React, { useState } from 'react';
import { RouteEntry, RoutesManifest } from '../types/manifest';
import {
  barcodeRfidCategories,
  techAccessoryCategories,
  partnerBrands,
  industrySolutions,
  homepageVideos
} from '../config/siteConfig';
import { SmartLink } from '../components/layout/SmartLink';
import { IconChevronRight } from '../components/icons/Icons';

export interface HomeTemplateProps {
  manifest: RoutesManifest;
  currentRoute: RouteEntry;
  onNavigate: (path: string) => void;
}

export const HomeTemplate: React.FC<HomeTemplateProps> = ({
  manifest,
  onNavigate
}) => {
  const [heroSlide, setHeroSlide] = useState<0 | 1>(0);
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);

  const allRoutes = manifest.routes;
  const posts = allRoutes.filter((r) => r.type === 'post' && r.indexable);

  return (
    <div className="container">
      {/* ======================================================================
          SECTION 1: WOODMART 50/50 HERO SLIDER & STACKED PROMO BANNERS (post-2025.css)
          ====================================================================== */}
      <section className="wd-hero-section">
        <div className="wd-hero-grid">
          {/* Left Column (50% width, 460px height): WoodMart Slider (#slider-119) */}
          <div className="wd-hero-slider-wrap">
            {heroSlide === 0 ? (
              /* Slide 1: Exact #slide-189 from https://pmcstech.net/ */
              <div className="wd-slide-card" style={{ backgroundColor: '#DDE8FA' }}>
                <img
                  src="/wp-content/uploads/2025/02/hero-phan-mem-kiem-ke-rfid-uhf.webp"
                  alt="Phần Mềm Kiểm Kê RFID"
                  className="wd-banner-bg-img"
                  width={1000}
                  height={657}
                  loading="eager"
                  fetchPriority="high"
                  decoding="sync"
                />
                <div className="wd-slide-inner" style={{ position: 'relative', zIndex: 2 }}>
                  <h1 className="wd-slide-title">Phần Mềm Kiểm Kê RFID</h1>
                  <div className="wd-slide-text">
                    <p>Kiểm kho hàng hóa, nữ trang bằng công nghệ UHF RFID</p>
                    <p>Kiểm đếm sản phẩm siêu tốc : 700 sản phẩm/ giây</p>
                    <p>Đối chiếu tồn kho sổ sách  khi quét.</p>
                  </div>
                  <div style={{ marginTop: '18px' }}>
                    <SmartLink
                      to="/cong-nghe-rfid/"
                      onNavigate={onNavigate}
                      className="wd-btn-primary"
                    >
                      <span>Xem Chi tiết</span>
                    </SmartLink>
                  </div>
                </div>
              </div>
            ) : (
              /* Slide 2: ECAMPMCS Packaging Video Solution */
              <div
                className="wd-slide-card"
                style={{
                  background: 'linear-gradient(135deg, #E3EEFF 0%, #F5F9FF 100%)'
                }}
              >
                <div className="wd-slide-inner wd-slide-inner-2">
                  <h1 className="wd-slide-title">Phần Mềm Quay Video Đóng Hàng</h1>
                  <div className="wd-slide-text">
                    <p>Ghi hình quá trình đóng gói &amp; chống gian lận hoàn hàng TMĐT</p>
                    <p>Kết nối 10 – 16 Camera trên 1 PC (CPU Core i3-14100)</p>
                    <p>Truy xuất video 3 giây &amp; chia sẻ qua CDN miễn phí.</p>
                  </div>
                  <div style={{ marginTop: '18px' }}>
                    <SmartLink
                      to="/product/phan-mem-quay-video-dong-hang-ecampmcs/"
                      onNavigate={onNavigate}
                      className="wd-btn-primary"
                    >
                      <span>Xem Chi tiết</span>
                    </SmartLink>
                  </div>
                </div>
                <img
                  src="/wp-content/uploads/2025/02/phan-mem-quay-video-dong-goi-hang-hoa-ecampmcs-2.jpg"
                  alt="Phần mềm quay video đóng hàng ECAMPMCS"
                  className="wd-slide-side-art"
                  width={320}
                  height={240}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            )}

            {/* Slider Prev/Next Chevrons */}
            <button
              type="button"
              className="wd-slider-nav-arrow prev"
              onClick={() => setHeroSlide(heroSlide === 0 ? 1 : 0)}
              aria-label="Previous slide"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              type="button"
              className="wd-slider-nav-arrow next"
              onClick={() => setHeroSlide(heroSlide === 0 ? 1 : 0)}
              aria-label="Next slide"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>

            {/* Slider Pagination Dots (pagin-style-3) */}
            <div className="wd-slide-dots">
              <button
                type="button"
                className={`wd-slide-dot ${heroSlide === 0 ? 'active' : ''}`}
                onClick={() => setHeroSlide(0)}
                aria-label="Slide 1: Phần Mềm Kiểm Kê RFID"
              />
              <button
                type="button"
                className={`wd-slide-dot ${heroSlide === 1 ? 'active' : ''}`}
                onClick={() => setHeroSlide(1)}
                aria-label="Slide 2: Phần Mềm Quay Video Đóng Hàng ECAMPMCS"
              />
            </div>
          </div>

          {/* Right Column (50% width, 277px + 20px + 163px = 460px): Stacked WoodMart Promo Banners */}
          <div className="wd-hero-right-stack">
            {/* Top Banner: Phụ Kiện Công Nghệ (277px height, elementor-element-41ba000) */}
            <div
              className="wd-promo-banner wd-promo-banner-top"
              onClick={() => onNavigate('/product-category/phu-kien-cong-nghe/')}
            >
              <img
                src="/wp-content/uploads/2023/01/logitech-aurora-headset-opt.jpg"
                alt="Phụ Kiện Công Nghệ"
                className="wd-banner-bg-img"
                width={680}
                height={277}
                loading="lazy"
                decoding="async"
              />
              <div className="wd-banner-content">
                <h2 className="wd-banner-title-top">Phụ Kiện Công Nghệ</h2>
                <p className="wd-banner-desc-top">
                  Âm thanh, đồng hồ, đế sạc, pin dự phòng...
                </p>
                <div>
                  <SmartLink
                    to="/product-category/phu-kien-cong-nghe/"
                    onNavigate={onNavigate}
                    className="wd-btn-primary"
                  >
                    <span>Xem Chi Tiết</span>
                  </SmartLink>
                </div>
              </div>
            </div>

            {/* Bottom Split 2-Column Banners (163px height each, elementor-element-f38ecf4 & 255593d) */}
            <div className="wd-promo-split-row">
              {/* Sub-Banner 1: Thiết Bị Mã Vạch (Full-bleed blue banner may-quet-ma-vach-banner.png) */}
              <div
                className="wd-promo-banner wd-promo-sub-banner"
                style={{ backgroundColor: '#003B63' }}
                onClick={() => onNavigate('/product-category/thiet-bi-ma-vach/')}
              >
                <img
                  src="/wp-content/uploads/2025/02/may-quet-ma-vach-banner-300x183.png"
                  alt="Thiết Bị Mã Vạch – Máy Kiểm Kho"
                  className="wd-banner-bg-img"
                  width={300}
                  height={183}
                  loading="eager"
                  decoding="async"
                />
                <div className="wd-banner-content wd-banner-content-sub">
                  <h2 className="wd-banner-title-sub" style={{ color: '#FFFFFF' }}>
                    Thiết Bị Mã Vạch
                  </h2>
                  <p className="wd-banner-desc-sub" style={{ color: '#FFFFFF' }}>
                    Máy Kiểm Kho
                  </p>
                  <div>
                    <SmartLink
                      to="/product-category/thiet-bi-ma-vach/"
                      onNavigate={onNavigate}
                      className="wd-btn-white-sm"
                    >
                      <span>Xem Chi Tiết</span>
                    </SmartLink>
                  </div>
                </div>
              </div>

              {/* Sub-Banner 2: Máy Đọc Thẻ RFID (Full-bleed yellow banner may-doc-the-rfid-banner.png) */}
              <div
                className="wd-promo-banner wd-promo-sub-banner"
                style={{ backgroundColor: '#FEF08A' }}
                onClick={() => onNavigate('/cong-nghe-rfid/')}
              >
                <img
                  src="/wp-content/uploads/2025/02/may-doc-the-rfid-banner-300x183.png"
                  alt="Máy Đọc Thẻ RFID – Máy Kiểm Kho RFID"
                  className="wd-banner-bg-img"
                  width={300}
                  height={183}
                  loading="eager"
                  decoding="async"
                />
                <div className="wd-banner-content wd-banner-content-sub">
                  <h2 className="wd-banner-title-sub" style={{ color: '#003B63', fontSize: '17px' }}>
                    Máy Đọc Thẻ RFID
                  </h2>
                  <p className="wd-banner-desc-sub" style={{ color: '#1E293B' }}>
                    Máy Kiểm Kho RFID
                  </p>
                  <div>
                    <SmartLink
                      to="/cong-nghe-rfid/"
                      onNavigate={onNavigate}
                      className="wd-btn-white-sm"
                    >
                      <span>Xem Chi Tiết</span>
                    </SmartLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================================================
          SECTION 2: 4-COLUMN SERVICE TRUST STRIP (elementor-element-fbe5e17 on #E4E4E4)
          ====================================================================== */}
      <section className="wd-trust-strip">
        <div className="wd-trust-box">
          <div className="wd-trust-circle-icon">
            {/* Star Icon */}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </div>
          <div>
            <div className="wd-trust-title">Sản Phẩm Chính Hãng</div>
            <p className="wd-trust-desc">Thương hiệu uy tín</p>
          </div>
        </div>

        <div className="wd-trust-box">
          <div className="wd-trust-circle-icon">
            {/* Award Ribbon Icon */}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="6" />
              <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
            </svg>
          </div>
          <div>
            <div className="wd-trust-title">Cam Kết Chất Lượng</div>
            <p className="wd-trust-desc">Sản phẩm nguồn gốc rõ ràng</p>
          </div>
        </div>

        <div className="wd-trust-box">
          <div className="wd-trust-circle-icon">
            {/* Wrench / Tools Icon */}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
            </svg>
          </div>
          <div>
            <div className="wd-trust-title">Bảo Hành 12-24 Tháng</div>
            <p className="wd-trust-desc">Quy trình nhanh chóng</p>
          </div>
        </div>

        <div className="wd-trust-box">
          <div className="wd-trust-circle-icon">
            {/* Care / Heart Hand Icon */}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
            </svg>
          </div>
          <div>
            <div className="wd-trust-title">Hỗ Trợ Tận Tâm</div>
            <p className="wd-trust-desc">Đội ngũ chuyên nghiệp</p>
          </div>
        </div>
      </section>

      {/* ======================================================================
          SECTION 3: THIẾT BỊ MÃ VẠCH & RFID (6 WoodMart cat-design-alt Cards)
          ====================================================================== */}
      <section className="wd-home-section">
        <div className="wd-section-header">
          <div className="wd-title-underlined">
            <h2>THIẾT BỊ MÃ VẠCH &amp; RFID</h2>
          </div>
        </div>

        <div className="wd-cat-grid-6">
          {barcodeRfidCategories.map((cat) => (
            <SmartLink
              key={cat.name}
              to={cat.path}
              onNavigate={onNavigate}
              className="wd-cat-item-alt"
            >
              <div className="wd-cat-white-box">
                <img src={cat.image} alt="" width={180} height={180} loading="lazy" decoding="async" />
              </div>
              <h3 className="wd-cat-label">{cat.name}</h3>
            </SmartLink>
          ))}
        </div>
      </section>

      {/* ======================================================================
          SECTION 4: PHỤ KIỆN CÔNG NGHỆ (6 WoodMart cat-design-alt Cards)
          ====================================================================== */}
      <section className="wd-home-section">
        <div className="wd-section-header">
          <div className="wd-title-underlined">
            <h2>PHỤ KIỆN CÔNG NGHỆ</h2>
          </div>
        </div>

        <div className="wd-cat-grid-6">
          {techAccessoryCategories.map((cat) => (
            <SmartLink
              key={cat.name}
              to={cat.path}
              onNavigate={onNavigate}
              className="wd-cat-item-alt"
            >
              <div className="wd-cat-white-box">
                <img src={cat.image} alt="" width={180} height={180} loading="lazy" decoding="async" />
              </div>
              <h3 className="wd-cat-label">{cat.name}</h3>
            </SmartLink>
          ))}
        </div>
      </section>

      {/* ======================================================================
          SECTION 5: GIẢI PHÁP QUAY VIDEO ĐÓNG HÀNG ECAMPMCS (elementor-element-978e497: 25% / 75%)
          ====================================================================== */}
      <section className="wd-home-section">
        <div className="wd-ecam-showcase-grid">
          {/* Left 25% Column: Uncropped Vertical Poster (ecampmcs-Banner-512x1024.png) */}
          <SmartLink
            to="/product/phan-mem-quay-video-dong-hang-ecampmcs/"
            onNavigate={onNavigate}
            className="wd-ecam-poster-link"
          >
            <img
              src="/wp-content/uploads/2025/02/ecampmcs-Banner-512x1024.png"
              alt="Phần mềm quay video đóng gói hàng hóa ECAMPMCS"
              width={512}
              height={1024}
              loading="lazy"
              decoding="async"
            />
          </SmartLink>

          {/* Right 75% Column: Heading + Wide Packing Photo + Image Box Content + Soft Blue Pill Button */}
          <div className="wd-ecam-right-col">
            <h2 className="wd-ecam-main-heading">
              <SmartLink
                to="/product/phan-mem-quay-video-dong-hang-ecampmcs/"
                onNavigate={onNavigate}
              >
                Giải Pháp Quay Video Đóng Hàng EcamPMCS
              </SmartLink>
            </h2>

            <SmartLink
              to="/product/phan-mem-quay-video-dong-hang-ecampmcs/"
              onNavigate={onNavigate}
              className="wd-ecam-wide-img-wrap"
            >
              <img
                src="/wp-content/uploads/2025/02/phan-mem-quay-video-dong-goi-hang-hoa-e1746798940108.jpg"
                alt="Phần mềm quay video đóng hàng ECAMPMCS"
                width={960}
                height={295}
                loading="lazy"
                decoding="async"
              />
            </SmartLink>

            <h3 className="wd-ecam-sub-heading">
              <SmartLink
                to="/product/phan-mem-quay-video-dong-hang-ecampmcs/"
                onNavigate={onNavigate}
              >
                ECAMPMCS – Phần mềm quay video đóng hàng chuyên nghiệp
              </SmartLink>
            </h3>

            <p className="wd-ecam-description">
              ECAMPMCS là giải pháp phần mềm giúp doanh nghiệp quay video quá trình đóng gói hàng hóa, Ngăn chặn các trường hợp khách hàng hoàn trả hàng hóa không đúng sản phẩm. Đồng thời Cung cấp hình ảnh làm bằng chứng cụ thể trong trường hợp mất mát hoặc tranh chấp với đơn vị giao nhận, giúp đẩy nhanh quá trình giải quyết khiếu nại.
              <br />
              <br />
              Phần mềm được phát triển bởi PMCS TECH và được ứng dụng rộng rãi trong các trung tâm vận hành thương mại điện tử, kho hàng, và dây chuyền sản xuất tại Việt Nam.
            </p>

            <div>
              <SmartLink
                to="/product/phan-mem-quay-video-dong-hang-ecampmcs/"
                onNavigate={onNavigate}
                className="wd-ecam-pill-btn"
              >
                <span>Xem Chi Tiết</span>
                <IconChevronRight size={14} />
              </SmartLink>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================================================
          SECTION 6: THƯƠNG HIỆU ƯA CHUỘNG (12 Real Brand Logos - elementor-element-04299f2)
          ====================================================================== */}
      <section className="wd-home-section">
        <div className="wd-section-header">
          <div className="wd-title-underlined">
            <h2>THƯƠNG HIỆU ƯA CHUỘNG</h2>
          </div>
        </div>

        <div className="wd-brand-logo-grid">
          {partnerBrands.map((b) => (
            <SmartLink
              key={b.name}
              to="/thuong-hieu/"
              onNavigate={onNavigate}
              className="wd-brand-logo-item"
            >
              <img src={b.logo} alt={b.name} width={175} height={66} loading="lazy" decoding="async" />
            </SmartLink>
          ))}
        </div>
      </section>

      {/* ======================================================================
          SECTION 7: THỊ TRƯỜNG ỨNG DỤNG (8 Promo Banners in 4x2 Mosaic - elementor-element-51fc689)
          ====================================================================== */}
      <section className="wd-home-section">
        <div className="wd-section-header">
          <div className="wd-title-underlined">
            <h2>THỊ TRƯỜNG ỨNG DỤNG</h2>
          </div>
        </div>

        <div className="wd-market-mosaic-grid">
          {industrySolutions.map((ind) => (
            <SmartLink
              key={ind.title}
              to={ind.path}
              onNavigate={onNavigate}
              className="wd-market-banner-tile"
            >
              <img src={ind.image} alt="" width={335} height={185} loading="lazy" decoding="async" />
              <div className="wd-market-tile-overlay">
                <h3 className="wd-market-tile-title">{ind.title}</h3>
              </div>
            </SmartLink>
          ))}
        </div>
      </section>

      {/* ======================================================================
          SECTION 8: BÀI VIẾT – TIN TỨC (3 WoodMart Blog Cards - elementor-element-d9a40dc)
          ====================================================================== */}
      <section className="wd-home-section">
        <div className="wd-section-header">
          <div className="wd-title-underlined">
            <h2>BÀI VIẾT - TIN TỨC</h2>
          </div>
        </div>

        <div className="grid-3">
          {posts
            .filter((p) => p.images && p.images.length > 0)
            .slice(0, 3)
            .map((post, idx) => {
              const thumb = post.images[0]?.localPath || '/wp-content/uploads/2025/02/LOGO-1-300x112.png';
              const dates = ['15 Jul 2026', '12 Jul 2026', '05 Jul 2026'];
            return (
              <article key={post.path} className="wd-blog-card">
                <SmartLink
                  to={post.path}
                  onNavigate={onNavigate}
                  className="wd-blog-card-thumb"
                >
                  <img src={thumb} alt={`Ảnh minh họa: ${post.title}`} width={440} height={260} loading="lazy" decoding="async" />
                </SmartLink>
                <div className="wd-blog-card-body">
                  <div className="wd-blog-date-text">{dates[idx] || '15 Jul 2026'}</div>
                  <h3 className="wd-blog-card-title">
                    <SmartLink to={post.path} onNavigate={onNavigate}>
                      {post.title}
                    </SmartLink>
                  </h3>
                  <p className="wd-blog-card-excerpt">
                    Hướng dẫn kỹ thuật và kinh nghiệm triển khai giải pháp quay video đóng gói hàng hóa ECAMPMCS, thiết bị mã vạch Opticon và RFID từ đội ngũ PMCS TECH...
                  </p>
                  <SmartLink
                    to={post.path}
                    onNavigate={onNavigate}
                    className="wd-blog-readmore"
                    aria-label={`Đọc chi tiết bài viết: ${post.title}`}
                  >
                    <span>Đọc chi tiết bài viết</span>
                  </SmartLink>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* ======================================================================
          SECTION 9: VIDEO SHOWCASE GRID (4 Videos - elementor-element-90d258b & f8a87fe)
          ====================================================================== */}
      <section className="wd-home-section">
        <div className="wd-section-header">
          <div className="wd-title-underlined">
            <h2>Video</h2>
          </div>
        </div>

        <div className="wd-video-grid-4">
          {homepageVideos.map((vid) => {
            const isPlaying = playingVideoId === vid.id;
            return (
              <div key={vid.id} className="wd-video-card">
                <div className="wd-video-frame">
                  {isPlaying ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${vid.youtubeId}?autoplay=1`}
                      title={vid.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ width: '100%', height: '100%', border: 'none' }}
                    />
                  ) : (
                    <button
                      type="button"
                      className="wd-video-thumb-btn"
                      onClick={() => setPlayingVideoId(vid.id)}
                      aria-label={`Play Video: ${vid.title}`}
                    >
                      <img src={vid.thumbnail} alt="" width={480} height={270} loading="lazy" decoding="async" />
                      <span className="wd-video-play-circle">
                        <svg viewBox="0 0 1000 1000" width="54" height="54" fill="#FFFFFF">
                          <path d="M838 162C746 71 633 25 500 25 371 25 258 71 163 162 71 254 25 367 25 500 25 633 71 746 163 837 254 929 367 979 500 979 633 979 746 933 838 837 929 746 975 633 975 500 975 367 929 254 838 162M808 192C892 279 933 379 933 500 933 621 892 725 808 808 725 892 621 938 500 938 379 938 279 896 196 808 113 725 67 621 67 500 67 379 108 279 196 192 279 108 383 62 500 62 621 62 721 108 808 192M438 392V642L642 517 438 392Z" />
                        </svg>
                      </span>
                    </button>
                  )}
                </div>
                <p className="wd-video-caption">{vid.title}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ======================================================================
          SECTION 10: NHẬN NGAY VOUCHER 10% OFF (elementor-element-4ac810cb)
          ====================================================================== */}
      <section className="wd-voucher-section">
        <div className="wd-voucher-grid">
          <div className="wd-voucher-left">
            <h2 className="wd-voucher-heading">Nhận Ngay Voucher 10% OFF</h2>
            <p className="wd-voucher-desc">
              Đăng ký email ngay ! Nhận Voucher giảm 10% cho đơn hàng đầu tiên &amp; cập nhật nhanh các chương trình khuyến mãi, nâng cấp hệ thống !
            </p>
            <p className="wd-voucher-note">
              * Voucher sẽ được gửi sau 24h, chỉ áp dụng cho khách hàng mới
            </p>
          </div>

          <div className="wd-voucher-right">
            <h3 className="wd-voucher-form-title">ĐĂNG KÝ NHẬN TIN KHUYẾN MÃI</h3>
            <form
              className="wd-voucher-form"
              onSubmit={(e) => {
                e.preventDefault();
                onNavigate('/lien-he/');
              }}
              {...({
                toolname: 'register_b2b_voucher_and_quote',
                tooldescription:
                  'Đăng ký nhận báo giá B2B, voucher khuyến mãi thiết bị mã vạch Opticon, phần mềm ECAMPMCS và quà tặng công nghệ LED từ PMCS TECH (pmcstech.net & pmcstech.com)'
              } as React.FormHTMLAttributes<HTMLFormElement>)}
            >
              <input
                type="text"
                name="customerName"
                title="Họ và tên người liên hệ hoặc tên doanh nghiệp đăng ký nhận tư vấn"
                className="wd-voucher-input"
                placeholder="Tên Bạn*"
                required
                aria-label="Tên Bạn"
              />
              <input
                type="email"
                name="customerEmail"
                title="Địa chỉ email doanh nghiệp để nhận bảng giá và voucher khuyến mãi"
                className="wd-voucher-input"
                placeholder="Địa chỉ email*"
                required
                aria-label="Địa chỉ email"
              />
              <div>
                <button type="submit" className="wd-btn-primary">
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};
